<template>
  <div
    v-if="normalizedHtml"
    class="ql-editor prepeers-ql-editor-content content-html"
    v-html="normalizedHtml" />
</template>

<script lang="ts" setup>
import { computed, type ComputedRef } from 'vue'
import type { PrepeersQlEditorContentProps } from '~/types/PrepeersQlEditorContent'
import {
  nosConseilsCategoryFromRouteSlug,
  rewriteLegacyContentLinks,
} from '~/utils/rewriteLegacyContentLinks'
import { normalizeQuillHtml } from '~/utils/quillHtml'

/**
 * Définit les props du composant PrepeersQlEditorContent.
 */
const props: PrepeersQlEditorContentProps = defineProps({
  html: {
    type: String,
    default: '',
  },
})

const route = useRoute()

const nosConseilsCategory: ComputedRef<string | undefined> = computed(() => {
  if (!route.path.includes('/nos-conseils')) return undefined
  return nosConseilsCategoryFromRouteSlug(route.params.slug)
})

const normalizedHtml: ComputedRef<string> = computed((): string => {
  const base = normalizeQuillHtml(props.html ?? '')
  return rewriteLegacyContentLinks(base, { nosConseilsCategory: nosConseilsCategory.value })
})
</script>

<style scoped>
.prepeers-ql-editor-content {
  margin: var(--space-8) 0;
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  color: var(--text-primary);
}

/* Neutralise les couleurs inline de l'éditeur Quill (ex. spans en noir). */
.prepeers-ql-editor-content :deep(span[style*='color']),
.prepeers-ql-editor-content :deep(p[style*='color']),
.prepeers-ql-editor-content :deep(li[style*='color']) {
  color: inherit !important;
}

/* ── Titres ─────────────────────────────────────────────────────── */
.prepeers-ql-editor-content :deep(h2) {
  font-size: var(--text-2xl);
  font-weight: 700;
  margin: var(--space-12) 0 var(--space-6) 0 !important;
  color: var(--text-primary);
}

.prepeers-ql-editor-content :deep(h3) {
  font-size: var(--text-lg);
  font-weight: 600;
  margin: var(--space-8) 0;
  color: var(--accent-blue-lt);
}

.prepeers-ql-editor-content :deep(h4) {
  font-size: var(--text-lg);
  font-weight: 600;
  margin: var(--space-8) 0;
  color: var(--accent-blue-lt);
}

.prepeers-ql-editor-content :deep(h5) {
  font-size: var(--text-base);
  font-weight: 600;
  margin: var(--space-6) 0;
  color: var(--accent-blue-lt);
}

/* ── Paragraphes ─────────────────────────────────────────────────── */
.prepeers-ql-editor-content :deep(p) {
  line-height: 32px;
  margin: 0 0 var(--space-4) 0;
  color: var(--text-secondary);
}

.prepeers-ql-editor-content :deep(p:last-child) {
  margin-bottom: 0;
}

.prepeers-ql-editor-content :deep(p:empty) {
  display: none;
  margin: 0;
  padding: 0;
}

/* ── Listes (pastille orange) ─────────────────────────────────────── */
.prepeers-ql-editor-content :deep(ul),
.prepeers-ql-editor-content :deep(ol) {
  margin: var(--space-4) 0 var(--space-6) 0;
  padding-left: var(--space-6);
  color: var(--text-secondary);
}

.prepeers-ql-editor-content :deep(ul) {
  list-style: none;
}

.prepeers-ql-editor-content :deep(ul) > li {
  position: relative;
  padding-left: var(--space-5);
  margin-bottom: var(--space-3);
  line-height: 32px;
}

.prepeers-ql-editor-content :deep(ul) > li::before {
  content: '';
  position: absolute;
  top: 12px;
  left: 0;
  width: 8px;
  height: 8px;
  background-color: #ff9100;
  border-radius: 50%;
}

.prepeers-ql-editor-content :deep(ol) {
  list-style: none;
  counter-reset: prepeers-ql-ol;
}

.prepeers-ql-editor-content :deep(ol) > li {
  position: relative;
  padding-left: var(--space-8);
  margin-bottom: var(--space-3);
  line-height: 32px;
  counter-increment: prepeers-ql-ol;
}

.prepeers-ql-editor-content :deep(ol) > li::before {
  content: counter(prepeers-ql-ol) '.';
  position: absolute;
  top: 0;
  left: 0;
  min-width: var(--space-6);
  font-weight: 700;
  color: var(--accent-blue-lt);
}

/* ── Liens (gradient bleu) ───────────────────────────────────────── */
.prepeers-ql-editor-content :deep(a) {
  background: var(--gradient-blue);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent !important;
  font-weight: 600;
  text-decoration: none;
  word-break: break-word;
}

.prepeers-ql-editor-content :deep(a:hover),
.prepeers-ql-editor-content :deep(a:focus-visible) {
  text-decoration: underline;
  text-decoration-color: var(--accent-blue);
  text-underline-offset: 3px;
}

.prepeers-ql-editor-content :deep(a.btn) {
  background: var(--gradient-blue) !important;
  background-clip: border-box !important;
  -webkit-background-clip: border-box !important;
  color: var(--text-primary) !important;
}

.prepeers-ql-editor-content :deep(a.btn:hover),
.prepeers-ql-editor-content :deep(a.btn:focus-visible) {
  text-decoration: none;
}

/* ── Tableaux ─────────────────────────────────────────────────────── */
.prepeers-ql-editor-content :deep(table) {
  width: 100%;
  margin: var(--space-6) 0;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-lg);
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.02);
  font-size: var(--text-base);
  color: var(--text-secondary);
}

.prepeers-ql-editor-content :deep(thead) {
  background: var(--gradient-blue);
}

.prepeers-ql-editor-content :deep(th) {
  padding: var(--space-3) var(--space-4);
  text-align: left;
  font-weight: 700;
  color: var(--text-primary);
  border: 0;
  letter-spacing: 0.01em;
}

.prepeers-ql-editor-content :deep(td) {
  padding: var(--space-3) var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  vertical-align: top;
  line-height: var(--leading-relaxed);
}

.prepeers-ql-editor-content :deep(tbody tr:first-child td) {
  border-top: 0;
}

.prepeers-ql-editor-content :deep(tbody tr:nth-child(even)) {
  background-color: rgba(255, 255, 255, 0.03);
}

.prepeers-ql-editor-content :deep(tbody tr:hover) {
  background-color: rgba(96, 200, 248, 0.06);
}

.prepeers-ql-editor-content :deep(tbody td:first-child) {
  font-weight: 600;
  color: var(--text-primary);
}

.prepeers-ql-editor-content :deep(table p) {
  margin: 0;
  line-height: var(--leading-relaxed);
}

@media (max-width: 768px) {
  .prepeers-ql-editor-content :deep(h2) {
    margin: var(--space-10) 0 var(--space-4) 0;
    font-size: var(--text-xl);
  }

  .prepeers-ql-editor-content :deep(h3) {
    margin: var(--space-6) 0;
    font-size: var(--text-base);
  }

  .prepeers-ql-editor-content :deep(table) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .prepeers-ql-editor-content :deep(th),
  .prepeers-ql-editor-content :deep(td) {
    padding: var(--space-2) var(--space-3);
    font-size: var(--text-sm);
  }
}
</style>
